import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  FiPlusCircle, 
  FiSearch, 
  FiFilter, 
  FiRefreshCw,
  FiCopy, 
  FiTrash2,
  FiBox,
  FiInfo,
  FiWifi,
  FiCheckCircle,
  FiClock
} from 'react-icons/fi';
import { getMacWhitelist, deleteMacAddress, fetchApplications, getMacAddressById } from '../../services/macWhitelistService';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';

const MacWhitelistList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // 状态
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [applicationId, setApplicationId] = useState(searchParams.get('applicationId') || '');
  const [activated, setActivated] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [macDetails, setMacDetails] = useState(null);

  // 获取应用列表
  const { data: applications } = useQuery('applications', fetchApplications);

  const handleApplicationChange = (e) => {
    setApplicationId(e.target.value);
    setPage(1);
    
    // 更新URL参数但不重载页面
    const params = new URLSearchParams(location.search);
    if (e.target.value) {
      params.set('applicationId', e.target.value);
    } else {
      params.delete('applicationId');
    }
    
    window.history.replaceState(
      {},
      '',
      `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`
    );
    
    refetch();
  };

  // 获取MAC地址白名单列表
  const { 
    data, 
    isLoading, 
    error, 
    refetch 
  } = useQuery(
    ['macWhitelist', page, limit, search, applicationId, activated],
    () => getMacWhitelist(page, limit, applicationId, search, activated),
    {
      keepPreviousData: true
    }
  );

  // 处理搜索
  const handleSearch = (e) => {
    e.preventDefault();
    refetch();
  };

  // 清除搜索
  const clearSearch = () => {
    setSearch('');
    refetch();
  };

  // 处理激活状态筛选
  const handleActivatedChange = (e) => {
    setActivated(e.target.value);
    setPage(1);
    refetch();
  };

  // 删除MAC地址
  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteMacAddress(deleteId);
      toast.success('MAC地址删除成功');
      refetch();
      setShowDeleteModal(false);
      setDeleteId(null);
    } catch (error) {
      toast.error(error.message || '删除MAC地址失败');
    }
  };

  const copytxt = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, 99999);
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  // 复制文本到剪贴板
  const copyToClipboard = (text) => {
    copytxt(text);
    toast.success('MAC地址已复制到剪贴板');
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '无';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  // 查看详情
  const handleViewDetails = async (id) => {
    try {
      const response = await getMacAddressById(id);
      setMacDetails(response.macAddress);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error(error.message || '获取MAC地址详情失败');
    }
  };

  return (
    <div className="h-full">
      {/* 页面标题 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">MAC地址报备</h1>
          <p className="text-gray-400 mt-1">管理MAC地址白名单，添加的MAC地址可通过verify-by-mac接口自动激活。</p>
        </div>
        <Button
          to="/mac-whitelist/create"
          variant="primary"
          icon={<FiPlusCircle />}
        >
          添加MAC地址
        </Button>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-slate-800 rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索MAC地址..."
                className="w-full py-2 px-4 pl-10 bg-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  &times;
                </button>
              )}
            </div>
          </form>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FiFilter className="text-gray-400" />
              <select
                value={applicationId}
                onChange={handleApplicationChange}
                className="bg-slate-700 rounded-lg text-white py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">所有应用</option>
                {applications?.map(app => (
                  <option key={app.id} value={app.id}>{app.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <FiFilter className="text-gray-400" />
              <select
                value={activated}
                onChange={handleActivatedChange}
                className="bg-slate-700 rounded-lg text-white py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全部状态</option>
                <option value="true">已激活</option>
                <option value="false">未激活</option>
              </select>
            </div>

            <Button
              onClick={() => refetch()}
              variant="secondary"
              icon={<FiRefreshCw />}
              className="px-3"
            >
              刷新
            </Button>
          </div>
        </div>
      </div>

      {/* MAC地址列表 */}
      <div className="bg-slate-800 rounded-xl shadow-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader size={50} />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-red-500 text-lg mb-4">获取MAC地址列表失败</div>
            <Button onClick={() => refetch()} variant="primary">
              重试
            </Button>
          </div>
        ) : data?.macAddresses?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FiWifi className="text-5xl mb-4" />
            <p className="text-xl">没有找到MAC地址</p>
            <p className="mt-2">尝试清除筛选条件或添加新MAC地址</p>
            <div className="mt-6 flex gap-4">
              <Button
                onClick={clearSearch}
                variant="secondary"
              >
                清除筛选
              </Button>
              <Button
                to="/mac-whitelist/create"
                variant="primary"
                icon={<FiPlusCircle />}
              >
                添加MAC地址
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-slate-700 text-gray-300">
                  <tr>
                    <th className="py-3 px-4 text-left">MAC地址</th>
                    <th className="py-3 px-4 text-left">所属应用</th>
                    <th className="py-3 px-4 text-left">描述</th>
                    <th className="py-3 px-4 text-left">激活时间</th>
                    <th className="py-3 px-4 text-left">创建时间</th>
                    <th className="py-3 px-4 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {data.macAddresses.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-700"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{item.macAddress}</span>
                          <button
                            onClick={() => copyToClipboard(item.macAddress)}
                            className="text-gray-400 hover:text-white"
                            title="复制MAC地址"
                          >
                            <FiCopy size={16} />
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {item.application ? (
                          <Link 
                            to={`/applications/${item.application.id}`}
                            className="px-2 py-1 text-xs rounded-full bg-blue-500 bg-opacity-20 text-blue-400 flex items-center inline-flex hover:bg-opacity-30"
                          >
                            <FiBox className="mr-1" />
                            {item.application.name}
                          </Link>
                        ) : (
                          <span className="text-gray-500">未知应用</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {item.description || <span className="text-gray-500">无</span>}
                      </td>
                      <td className="py-3 px-4">
                        {item.activatedAt ? (
                          <div className="flex items-center gap-1 text-green-400">
                            <FiCheckCircle size={14} />
                            <span>{formatDate(item.activatedAt)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-gray-500">
                            <FiClock size={14} />
                            <span>未激活</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-gray-300">
                          <FiInfo size={14} />
                          <span>{formatDate(item.createdAt)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          {item.activatedAt && (
                            <button
                              onClick={() => handleViewDetails(item.id)}
                              className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900 hover:bg-opacity-30 rounded-md transition-colors"
                              title="查看激活详情"
                            >
                              <FiInfo size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setDeleteId(item.id);
                              setShowDeleteModal(true);
                            }}
                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900 hover:bg-opacity-30 rounded-md transition-colors"
                            title="删除MAC地址"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            <div className="p-4 flex justify-between items-center border-t border-slate-700">
              <div className="text-gray-400">
                总计 {data.totalItems} 个MAC地址，共 {data.totalPages} 页
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage(page > 1 ? page - 1 : 1)}
                  disabled={page <= 1}
                  variant="secondary"
                  size="sm"
                >
                  上一页
                </Button>
                <span className="inline-flex items-center px-3 py-1 rounded bg-slate-700 text-white">
                  {page} / {data.totalPages || 1}
                </span>
                <Button
                  onClick={() => setPage(page < data.totalPages ? page + 1 : data.totalPages)}
                  disabled={page >= data.totalPages}
                  variant="secondary"
                  size="sm"
                >
                  下一页
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 删除确认弹窗 */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="删除MAC地址"
      >
        <div className="mb-6">
          <p className="text-gray-300">确定要从白名单中删除这个MAC地址吗？此操作不可逆。</p>
        </div>
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => setShowDeleteModal(false)}
            variant="secondary"
          >
            取消
          </Button>
          <Button
            onClick={handleDelete}
            variant="danger"
            icon={<FiTrash2 />}
          >
            删除
          </Button>
        </div>
      </Modal>

      {/* 激活详情弹窗 */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setMacDetails(null);
        }}
        title="MAC地址激活详情"
      >
        {macDetails && (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">MAC地址</label>
              <div className="bg-slate-700 rounded-lg py-2 px-3 text-white font-mono">
                {macDetails.macAddress}
              </div>
            </div>
            
            {macDetails.application && (
              <div>
                <label className="block text-gray-400 text-sm mb-1">所属应用</label>
                <div className="bg-slate-700 rounded-lg py-2 px-3 text-white">
                  {macDetails.application.name} ({macDetails.application.appId})
                </div>
              </div>
            )}

            {macDetails.name && (
              <div>
                <label className="block text-gray-400 text-sm mb-1">设备名称</label>
                <div className="bg-slate-700 rounded-lg py-2 px-3 text-white">
                  {macDetails.name}
                </div>
              </div>
            )}

            {macDetails.deviceId && (
              <div>
                <label className="block text-gray-400 text-sm mb-1">设备ID</label>
                <div className="bg-slate-700 rounded-lg py-2 px-3 text-white font-mono">
                  {macDetails.deviceId}
                </div>
              </div>
            )}

            {macDetails.description && (
              <div>
                <label className="block text-gray-400 text-sm mb-1">描述</label>
                <div className="bg-slate-700 rounded-lg py-2 px-3 text-white">
                  {macDetails.description}
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-400 text-sm mb-1">激活时间</label>
              <div className="bg-slate-700 rounded-lg py-2 px-3 text-white">
                {formatDate(macDetails.activatedAt)}
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">创建时间</label>
              <div className="bg-slate-700 rounded-lg py-2 px-3 text-white">
                {formatDate(macDetails.createdAt)}
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            onClick={() => {
              setShowDetailsModal(false);
              setMacDetails(null);
            }}
            variant="secondary"
          >
            关闭
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default MacWhitelistList;

